/**
 * Take all bands in parameters, output base64 svg
 * Output example : https://ideine-my.sharepoint.com/:i:/g/personal/thierry_levisse_ideine_fr/EVPE9m3CMgxHqO9-mDJqRaUBWkUxy3DllRJIa6kG-vmivw?e=gAiDjQ
 * @constructor
 */
import {
    BandsFromBandIdContent,
    getImageBandName,
} from 'Plugins/FloorPlan/Components/Laizes/Dialog/ZoomDialogs/BandsFromBandIdDialog';
import { BandType, LaizeCalculatorResult } from 'Plugins/FloorPlan/Components/Laizes/Laize';
import { LaizeBandItemV3 } from 'Plugins/FloorPlan/Components/Laizes/v3/LaizeBandTrace';
import { IRoom, RoomType } from 'Plugins/FloorPlan/Models/IRoom';
import React from 'react';

import { MapContextState } from '../../../../Context/MapContext';
import { ExportMapImageHelper } from '../../Helpers/ExportMapPngHelper';
import { SvgHelpers } from '../../Helpers/SvgGenerationHelpers';
import { ExportBandsFromBandId, SvgByBandId } from '../LaizeExportBands';
import { getProduitPrincipal } from 'Web/Pages/Quote/Utils/UpdateQuestions';

export const useExportMapBandsFromBandIdStudio = (
    { rooms = [], laizeResults = [], busy }: Partial<MapContextState>,
    onlyRoomType?: RoomType
) => {
    const roomIds = rooms
        .filter((room) => (onlyRoomType ? room.type === onlyRoomType : true))
        .map((room) => room.roomId);

    const bands: LaizeBandItemV3[] = laizeResults
        .filter((x) => x.result)
        .map((x) => x.calculator)
        .flatMap((x) => x?.bands_v3 || [])
        .filter((x) => x?.type === BandType.New && roomIds.includes(x.roomId));

    const svgHolders = React.useRef<SvgByBandId[]>([]);
    const [exportItems, setExportItems] = React.useState<ExportBandsFromBandId[]>([]);

    React.useEffect(() => {
        svgHolders.current = bands.map((band) => ({ id: band.id }));
        if (busy || !laizeResults.length) {
            setExportItems([]);
        } else {
            setExportItems(
                bands.map((band) =>
                    Preview({
                        band,
                        rooms,
                        laizeResult: laizeResults.find((x) => x.roomIds?.some((y) => y === band.roomId))?.calculator,
                        getHolder: () => svgHolders.current.find((r) => r.id === band.id)!,
                    })
                )
            );
        }
    }, [busy, bands.length, laizeResults.length]);

    return { exportItems };
};

type ExportBandsFromBandIdParams = {
    band: LaizeBandItemV3;
    rooms: IRoom[];
    laizeResult?: LaizeCalculatorResult;
    getHolder: () => SvgByBandId;
};

export const Preview = (props: ExportBandsFromBandIdParams): ExportBandsFromBandId => {
    const { band, rooms, laizeResult, getHolder } = props;
    const { index, imageName, room } = getImageBandName(band, rooms, laizeResult);
    const { produitPrincipal } = getProduitPrincipal(room);

    const code_qct = produitPrincipal?.code!;
    const description = `bande_${index}`;
    const bandFileName = `${description}_${code_qct}`;

    const captureSvg = (toBase64: boolean = true) => {
        const holder = getHolder();
        return SvgHelpers.capture(holder?.svgWrapper?.current?.outerHTML, toBase64);
    };

    const capturePng = (toBase64: boolean = true) => {
        const imageSrc = captureSvg(toBase64);
        return ExportMapImageHelper.exportMapPng(imageSrc);
    };

    return {
        code_qct,
        description,
        imageName,
        bandFileName,
        captureSvg,
        capturePng,
        getHolder,
        renderView: ({ id, style }: React.HTMLAttributes<any> = {}) => (
            <BandsFromBandIdContent
                key={id}
                setSvgWrapper={(ref) => {
                    const holder = getHolder();
                    if (holder) {
                        holder.svgWrapper = ref;
                    }
                }}
                band={band}
                rooms={rooms}
                laizeResult={laizeResult!}
                style={style}
                exportMode
            />
        ),
    };
};
